import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Sun,
  ArrowRight,
  Check,
  Battery,
  Zap,
  Star,
  TrendingUp,
  Shield,
  Users,
  Lightbulb,
  MapPin,
  ExternalLink,
} from "lucide-react";
import {
  HERO_BADGES,
  STATS,
  PLANS,
  WHY_US,
  TESTIMONIALS,
  SITE,
} from "../data/mock";
import Seo from "../components/Seo";
import HeroCarousel from "../components/HeroCarousel";
import Reveal from "../components/Reveal";
import { ACCENT_THEME, planSlug, roleIcon } from "../components/PlanParts";
import AnimatedNumber from "../components/AnimatedNumber";
import TestimonialsCarousel from "../components/TestimonialsCarousel";

const iconMap = {
  zap: Zap,
  star: Star,
  trending: TrendingUp,
  shield: Shield,
  users: Users,
};

const HeroSection = () => (
  <section className="hero-section relative flex items-center bg-[#0A1F44] overflow-hidden">
    {/* Pattern grid dots di sfondo — molto sottile, aggiunge textura */}
    <div
      aria-hidden
      className="absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage:
          "radial-gradient(rgba(255,255,255,0.9) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    />
    {/* Gradient radiale + blob animati */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="hero-blob-1 absolute top-[-4rem] left-[-4rem] w-[28rem] h-[28rem] bg-[#F4C542] rounded-full blur-[140px] opacity-20" />
      <div className="hero-blob-2 absolute bottom-[-6rem] right-[-4rem] w-[32rem] h-[32rem] bg-[#0FB36B] rounded-full blur-[140px] opacity-15" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[60rem] h-[60rem] rounded-full opacity-[0.06]"
           style={{
             background:
               "radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 55%)",
           }}
      />
    </div>

    {/* Gradient overlay in basso per stacco con la sezione successiva */}
    <div
      aria-hidden
      className="absolute bottom-0 inset-x-0 h-24 bg-gradient-to-t from-[#0A1F44] to-transparent"
    />

    <div className="relative mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div className="text-white">
          {/* Eyebrow con linea decorativa a lato */}
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-gradient-to-r from-[#F4C542] to-transparent" />
            <div className="inline-flex items-center gap-2 bg-white/[0.07] backdrop-blur-sm border border-white/10 rounded-full px-3.5 py-1.5">
              <Sun className="w-3.5 h-3.5 text-[#F4C542]" />
              <span className="text-[11px] sm:text-xs text-[#F4C542] font-montserrat font-semibold tracking-[0.15em] uppercase">
                {SITE.motto}
              </span>
            </div>
          </div>

          <h1 className="text-[2.5rem] sm:text-6xl lg:text-7xl mb-6 leading-[1.02] font-montserrat font-bold tracking-tight">
            Risparmia sull'energia,{" "}
            <span className="bg-gradient-to-r from-[#F4C542] via-[#f8d16e] to-[#F4C542] bg-clip-text text-transparent">
              investi nel futuro
            </span>
          </h1>

          <p className="text-base sm:text-lg text-gray-300/90 mb-8 leading-relaxed max-w-xl">
            Consulenti energetici specializzati in impianti fotovoltaici e soluzioni per l'efficienza energetica.
            Impianti chiavi in mano con sistemi di accumulo, progettati su misura per abitazioni e aziende.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <Link
              to="/contatti"
              className="group relative inline-flex items-center justify-center gap-2 bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] rounded-lg px-6 h-12 font-montserrat font-bold text-sm shadow-lg shadow-[#F4C542]/25 hover:shadow-[#F4C542]/45 hover:-translate-y-0.5 transition-all duration-300 overflow-hidden"
            >
              {/* Sheen effect al hover */}
              <span
                aria-hidden
                className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              />
              <span className="relative">Richiedi Preventivo Gratuito</span>
              <ArrowRight className="relative w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/progetti"
              className="group inline-flex items-center justify-center gap-2 bg-transparent text-white hover:bg-white/[0.08] border border-white/25 hover:border-white/50 rounded-lg px-6 h-12 font-montserrat font-semibold text-sm transition-all duration-300"
            >
              Vedi i Nostri Lavori
              <ArrowRight className="w-4 h-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm max-w-md mt-2">
            {HERO_BADGES.map((b) => (
              <div key={b} className="flex items-center gap-2">
                <div className="w-4 h-4 rounded-full bg-[#0FB36B]/20 flex items-center justify-center flex-shrink-0">
                  <Check className="w-2.5 h-2.5 text-[#0FB36B]" strokeWidth={3} />
                </div>
                <span className="text-gray-300/90 text-xs sm:text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          {/* Glow dietro il carousel */}
          <div
            aria-hidden
            className="absolute inset-0 bg-gradient-to-tr from-[#F4C542]/20 to-[#0FB36B]/10 rounded-2xl blur-2xl scale-[0.95] -z-10"
          />
          <HeroCarousel />

          {/* Card "80% risparmio" — piu' grande e elegante */}
          <div className="absolute -bottom-5 -left-4 sm:-bottom-7 sm:-left-6 bg-white/95 backdrop-blur-md p-4 sm:p-5 rounded-2xl shadow-2xl ring-1 ring-black/5 pointer-events-none">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#F4C542] to-[#e6b838] rounded-xl flex items-center justify-center shadow-md shadow-[#F4C542]/30">
                <Battery className="w-6 h-6 sm:w-7 sm:h-7 text-[#0A1F44]" />
              </div>
              <div>
                <p className="text-3xl sm:text-4xl text-[#0A1F44] font-montserrat font-bold leading-none tracking-tight">
                  <AnimatedNumber value="80%" />
                </p>
                <p className="text-[11px] sm:text-xs text-gray-500 mt-1.5 font-semibold uppercase tracking-wider">
                  Risparmio in Bolletta
                </p>
              </div>
            </div>
          </div>

          {/* Seconda card — anti-blackout */}
          <div className="hidden sm:flex absolute -top-4 -right-4 bg-white/95 backdrop-blur-md p-3.5 rounded-2xl shadow-2xl ring-1 ring-black/5 items-center gap-3 pointer-events-none">
            <div className="w-10 h-10 bg-gradient-to-br from-[#0FB36B] to-[#0d9a5c] rounded-lg flex items-center justify-center shadow-md shadow-[#0FB36B]/25">
              <Zap className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-lg text-[#0A1F44] font-montserrat font-bold leading-none tracking-tight">
                24/7
              </p>
              <p className="text-[10px] text-gray-500 mt-0.5 font-semibold uppercase tracking-wider">
                Energia sempre
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const StatsSection = () => (
  <section className="py-12 sm:py-16 bg-white border-b border-gray-100">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-y-8 gap-x-6 sm:gap-8 divide-y sm:divide-y-0 sm:divide-x divide-gray-100">
        {STATS.map((s, i) => {
          const Icon = iconMap[s.icon] || Zap;
          return (
            <Reveal
              key={s.label}
              delay={i * 80}
              className="text-center sm:px-4 pt-8 first:pt-0 sm:pt-0"
            >
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#0FB36B]/10 mb-3">
                <Icon className="w-5 h-5 text-[#0FB36B]" />
              </div>
              <p className="text-3xl sm:text-4xl text-[#0A1F44] mb-1.5 font-montserrat font-bold tracking-tight tabular-nums">
                <AnimatedNumber value={s.value} />
              </p>
              <p className="text-[11px] sm:text-xs text-gray-500 uppercase tracking-widest font-semibold">
                {s.label}
              </p>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const MapCalloutSection = () => (
  <section className="py-10 sm:py-12 bg-white border-b border-gray-100">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Link
        to="/progetti#mappa"
        className="group flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 bg-gradient-to-r from-[#0A1F44] to-[#0d2855] rounded-2xl p-6 sm:p-7 text-white hover:shadow-xl transition-all duration-300 overflow-hidden relative"
      >
        <div
          aria-hidden
          className="absolute -top-16 -right-16 w-52 h-52 bg-[#F4C542] rounded-full blur-[100px] opacity-15 group-hover:opacity-25 transition-opacity"
        />
        <div className="relative flex items-center justify-center w-14 h-14 rounded-xl bg-[#F4C542]/15 flex-shrink-0">
          <MapPin className="w-7 h-7 text-[#F4C542]" />
        </div>
        <div className="relative flex-1">
          <p className="text-xs text-[#F4C542] font-montserrat font-semibold tracking-[0.15em] uppercase mb-1">
            Dove lavoriamo
          </p>
          <h3 className="text-xl sm:text-2xl font-montserrat font-bold leading-tight">
            Scopri la mappa dei posti in cui abbiamo lavorato
          </h3>
          <p className="text-sm text-gray-300 mt-1">
            Ogni pin è un'installazione reale. Clicca per vedere il progetto.
          </p>
        </div>
        <div className="relative inline-flex items-center gap-2 bg-[#F4C542] text-[#0A1F44] rounded-md px-5 h-11 font-montserrat font-bold text-sm group-hover:bg-[#e6b838] transition-colors">
          Vedi la mappa
          <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </div>
      </Link>
    </div>
  </section>
);

const PlanBand = ({ p, idx }) => {
  const theme = ACCENT_THEME[p.accent] || ACCENT_THEME.green;
  const slug = planSlug(p);
  return (
    <Reveal
      delay={idx * 120}
      className={`relative bg-white rounded-3xl overflow-hidden ${theme.border} ${theme.ring} transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl`}
    >
      <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${theme.stripe}`} aria-hidden />
      <div className="grid lg:grid-cols-[1fr,minmax(0,380px)] gap-6 lg:gap-10 pl-8 pr-6 sm:pr-8 py-7 lg:py-8 items-center">
        {/* LEFT: identita' del listino */}
        <div>
          {p.badge && (
            <span
              className={`inline-block px-2.5 py-1 mb-3 rounded-md text-[10px] font-montserrat font-bold tracking-[0.22em] uppercase ${theme.badge}`}
            >
              {p.badge}
            </span>
          )}
          <h3 className="text-4xl sm:text-5xl font-montserrat font-extrabold text-[#0A1F44] leading-none mb-1">
            {p.name}
          </h3>
          <div
            className={`text-[12px] font-montserrat font-semibold tracking-[0.08em] uppercase ${theme.tagText}`}
          >
            {p.tagline}
          </div>
          <p className="mt-3 text-[14px] text-gray-600 leading-relaxed max-w-xl">
            {p.subtitle}
          </p>
        </div>

        {/* RIGHT: riepilogo componenti + accesso alla pagina del listino */}
        <div>
          <ul className="mb-4 divide-y divide-gray-100 rounded-2xl border border-gray-200 bg-gray-50/60">
            {p.onRequest ? (
              <li className="px-4 py-3 text-[13px] text-gray-600">
                {p.onRequest.title}
              </li>
            ) : (
              p.components.map((c) => {
                const Icon = roleIcon(c.role);
                return (
                  <li key={`${p.name}-${c.role}`} className="flex items-center gap-3 px-4 py-2.5">
                    <span className="inline-flex w-7 h-7 items-center justify-center rounded-lg bg-[#0A1F44]/[0.06] text-[#0A1F44] shrink-0">
                      <Icon className="w-4 h-4" />
                    </span>
                    <span className="text-[10px] w-[5.5rem] shrink-0 font-montserrat font-bold tracking-[0.15em] uppercase text-gray-500">
                      {c.role}
                    </span>
                    <span className="text-[14px] font-montserrat font-bold text-[#0A1F44] truncate">
                      {c.brand}
                    </span>
                  </li>
                );
              })
            )}
          </ul>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={`/listini/${slug}`}
              className={`inline-flex w-full sm:w-auto sm:flex-1 shrink-0 items-center justify-center gap-2 rounded-xl h-12 text-sm font-montserrat font-bold shadow-sm hover:shadow-md transition-all ${theme.cta}`}
            >
              Scopri il listino
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contatti"
              className="inline-flex w-full sm:w-auto sm:flex-1 shrink-0 items-center justify-center rounded-xl h-12 text-sm font-montserrat font-bold border border-[#0A1F44]/20 text-[#0A1F44] hover:border-[#0A1F44] transition-colors"
            >
              {p.ctaLabel || "Richiedi preventivo"}
            </Link>
          </div>
        </div>
      </div>
    </Reveal>
  );
};


const PricingSection = () => (
  <section
    className="relative pt-16 pb-10 sm:pt-24 sm:pb-12 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden"
    id="offerte"
  >
    <div
      className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(244,197,66,0.12)_0%,transparent_70%)]"
      aria-hidden
    />
    <div
      className="pointer-events-none absolute -bottom-32 left-1/2 -translate-x-1/2 w-[720px] h-72 rounded-full bg-[#0FB36B]/10 blur-3xl"
      aria-hidden
    />
    <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center mb-14">
        <div className="inline-flex items-center gap-2 bg-[#0FB36B]/10 rounded-full px-4 py-1.5 mb-4">
          <Sun className="w-4 h-4 text-[#0FB36B]" />
          <span className="text-xs text-[#0FB36B] font-montserrat font-semibold tracking-wider">
            OFFERTE FOTOVOLTAICO
          </span>
        </div>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-4 text-[#0A1F44] font-montserrat font-bold">
          Scegli la Tua Soluzione
        </h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
          Quattro configurazioni chiavi in mano con moduli, inverter e accumulo dimensionati per la tua abitazione. Componenti selezionati dai migliori brand del settore.
        </p>
      </Reveal>

      <div className="space-y-6">
        {PLANS.map((p, idx) => (
          <PlanBand key={p.name} p={p} idx={idx} />
        ))}
      </div>
    </div>
  </section>
);

const SuggestionSection = () => (
  <section className="py-16 sm:py-20 bg-white">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="bg-gradient-to-br from-[#0A1F44] to-[#0d2855] rounded-2xl p-6 sm:p-10 lg:p-14 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-64 h-64 bg-[#F4C542] rounded-full blur-[100px] opacity-10" />
        <div className="relative grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#F4C542]/15 rounded-full px-4 py-1.5 mb-4">
              <Lightbulb className="w-4 h-4 text-[#F4C542]" />
              <span className="text-xs text-[#F4C542] font-montserrat font-semibold tracking-wider">
                SUGGERIMENTO PER TE
              </span>
            </div>
            <h2 className="text-3xl sm:text-4xl mb-4 font-montserrat font-bold">
              Hai esigenze energetiche particolari?
            </h2>
            <p className="text-gray-300 mb-6 leading-relaxed">
             Realizziamo impianti fotovoltaici personalizzati in base ai consumi reali della tua abitazione o attività. 
             Dalla configurazione standard fino a soluzioni ad alta autonomia con accumulo maggiorato, studiamo la soluzione 
             più adatta alle tue esigenze.
            </p>
            <ul className="space-y-2 mb-6">
              {[
                "Progettazione su misura",
                "Sistemi di accumulo personalizzabili",
                "Soluzioni per abitazioni, aziende e attività commerciali",
                "Consulenza dedicata",
              ].map((t) => (
                <li key={t} className="flex items-center gap-2 text-sm">
                  <Check className="w-4 h-4 text-[#0FB36B] flex-shrink-0" />
                  <span className="text-gray-300">{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/contatti"
              className="group inline-flex items-center justify-center gap-2 bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] rounded-md px-6 h-11 font-montserrat font-bold text-sm transition-colors"
            >
              Richiedi Preventivo 9 kW
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
          <div className="hidden lg:block">
            <img
              src="	https://drive.google.com/thumbnail?id=12TbZsvQkxpxYWLICz4LL9SXBpl-TF01c&sz=w1600"
              alt="Impianto Fotovoltaico"
              className="w-full rounded-xl shadow-lg object-cover aspect-[4/3]"
            />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const WhySection = () => {
  // Layout bento: card 0 grande a sx (span rows), card 1 wide in alto,
  // card 2 e 3 piccole in basso.
  const layouts = [
    "md:col-span-1 md:row-span-2 md:p-8",
    "md:col-span-2",
    "md:col-span-1",
    "md:col-span-1",
  ];
  return (
    <section className="py-20 sm:py-24 bg-gray-50 relative overflow-hidden">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative">
        <Reveal className="text-center mb-14 max-w-2xl mx-auto">
          <p className="text-xs text-[#0FB36B] font-montserrat font-bold tracking-[0.25em] uppercase mb-3">
            Perché noi
          </p>
          <h2 className="text-4xl sm:text-5xl mb-5 text-[#0A1F44] font-montserrat font-bold tracking-tight leading-tight">
            La qualità che <span className="italic font-serif text-[#0FB36B]">senti</span>,
            <br className="hidden sm:block" /> il risparmio che <span className="italic font-serif text-[#F4C542]">vedi</span>.
          </h2>
          <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
            Consulenti energetici al tuo fianco per un risparmio reale e duraturo.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-fr">
          {WHY_US.map((w, i) => {
            const Icon = iconMap[w.icon] || Shield;
            const isYellow = w.color === "yellow";
            const isFeatured = i === 0;
            return (
              <Reveal
                key={w.title}
                delay={i * 100}
                className={`group relative bg-white rounded-2xl border border-gray-100 p-6 sm:p-7 overflow-hidden hover:border-gray-200 hover:shadow-[0_20px_40px_-15px_rgba(10,31,68,0.15)] transition-all duration-500 ${layouts[i] || ""}`}
              >
                {/* Decorazione angolo (visibile su card featured) */}
                {isFeatured && (
                  <div
                    aria-hidden
                    className="absolute -top-24 -right-24 w-48 h-48 rounded-full blur-3xl opacity-40 group-hover:opacity-60 transition-opacity duration-700"
                    style={{
                      backgroundColor: isYellow ? "#F4C542" : "#0FB36B",
                    }}
                  />
                )}
                <div className="relative flex flex-col h-full">
                  <div
                    className={`inline-flex items-center justify-center rounded-2xl mb-5 transition-transform duration-500 group-hover:scale-105 ${
                      isFeatured ? "w-14 h-14" : "w-12 h-12"
                    }`}
                    style={{
                      backgroundColor: isYellow
                        ? "rgba(244, 197, 66, 0.14)"
                        : "rgba(15, 179, 107, 0.14)",
                    }}
                  >
                    <Icon
                      className={isFeatured ? "w-7 h-7" : "w-6 h-6"}
                      style={{ color: isYellow ? "#F4C542" : "#0FB36B" }}
                    />
                  </div>
                  <h3
                    className={`text-[#0A1F44] font-montserrat font-bold leading-tight mb-3 tracking-tight ${
                      isFeatured ? "text-2xl sm:text-3xl" : "text-lg"
                    }`}
                  >
                    {w.title}
                  </h3>
                  <p
                    className={`text-gray-600 leading-relaxed ${
                      isFeatured ? "text-base" : "text-sm"
                    }`}
                  >
                    {w.desc}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const TestimonialsSection = () => (
  <section className="py-16 sm:py-20 bg-white">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <Reveal className="text-center mb-12">
        <h2 className="text-3xl sm:text-4xl mb-4 text-[#0A1F44] font-montserrat font-bold">
          Cosa Dicono i Nostri Clienti
        </h2>
        <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
          Oltre 500 famiglie e aziende hanno scelto ENERGEIDE per il loro
          impianto fotovoltaico
        </p>
      </Reveal>
      <Reveal>
        <TestimonialsCarousel fallback={TESTIMONIALS} />
      </Reveal>
      <div className="text-center mt-10">
        <p className="text-sm text-gray-500 mb-5">
          Risparmio medio annuale:{" "}
          <strong className="text-[#0FB36B]">€1.000+</strong> per cliente
        </p>
        <a
          href="https://it.trustpilot.com/review/energeide.it"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center justify-center gap-3 bg-[#00B67A] hover:bg-[#009E69] text-white rounded-md px-6 h-12 font-montserrat font-bold text-sm transition-colors shadow-md hover:shadow-lg"
        >
          <span className="inline-flex items-center gap-1.5 pr-3 mr-1 border-r border-white/30">
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="white"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
            <span className="font-bold tracking-tight text-[15px]">
              Trustpilot
            </span>
          </span>
          Scrivi una recensione su di noi
          <ExternalLink className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
        </a>
        <p className="text-xs text-gray-400 mt-2">La tua opinione conta!</p>
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="py-16 sm:py-20 bg-[#0A1F44] text-white relative overflow-hidden">
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-0 right-0 w-72 h-72 bg-[#F4C542] rounded-full blur-[120px] opacity-10" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#0FB36B] rounded-full blur-[120px] opacity-10" />
    </div>
    <Reveal className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8 text-center">
      <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-5 font-montserrat font-bold">
        Inizia a Risparmiare Oggi
      </h2>
      <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed">
        Preventivo gratuito e personalizzato in 24 ore. I nostri consulenti ti
        guideranno verso l'indipendenza energetica.
      </p>
      <Link
        to="/contatti"
        className="group inline-flex items-center justify-center gap-2 bg-[#F4C542] hover:bg-[#e6b838] text-[#0A1F44] rounded-md px-6 h-11 font-montserrat font-bold text-sm shadow-lg shadow-[#F4C542]/20 transition-colors"
      >
        Richiedi Preventivo Gratuito
        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
      </Link>
      <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs sm:text-sm text-gray-400">
        {["Sopralluogo Gratuito", "Nessun Obbligo", "Risposta in 24h"].map(
          (i) => (
            <div key={i} className="flex items-center gap-1.5">
              <Check className="w-4 h-4 text-[#0FB36B]" />
              <span>{i}</span>
            </div>
          )
        )}
      </div>
    </Reveal>
  </section>
);

// Torna dalle pagine dei listini direttamente alla sezione offerte (/#offerte)
const ScrollToHash = () => {
  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => {
      document.getElementById(hash.slice(1))?.scrollIntoView({ behavior: "auto", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, [hash]);
  return null;
};

const Home = () => (
  <>
    <Seo path="/" />
    <ScrollToHash />
    <HeroSection />
    <StatsSection />
    <MapCalloutSection />
    <PricingSection />
    <SuggestionSection />
    <WhySection />
    <TestimonialsSection />
    <CTASection />
  </>
);

export default Home;
